import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListStockAdjustmentsInput, ListWaitingDifferencesInput } from "@nexxt/contracts/logistics";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the adjustments of stock the IMS proposes and of the counts that wait (50-api/logistics.md, ADR-226):
// one factory, the same keys for loaders, screens and mutations. The counts that wait keep their keys under the
// adjustments, so whatever reloads the adjustments reloads them too.

const adjustments = api.logistics["stock-adjustments"];
const waiting = api.logistics["waiting-differences"];

type ListPage = InferResponseType<typeof adjustments.$get, 200>;
export type StockAdjustment = ListPage["items"][number];
export type StockAdjustmentDetail = InferResponseType<(typeof adjustments)[":adjustmentId"]["$get"], 200>;
export type WaitingDifference = InferResponseType<typeof waiting.$get, 200>["items"][number];

/** The filters of the list input (`listStockAdjustmentsInput`), without the paging the queries add. */
export type StockAdjustmentFilters = Pick<ListStockAdjustmentsInput, "status" | "location" | "q"> & {
  /** The sets of the queues "To do", as `Todo` writes them (ADR-302). */
  anyOf?: string | undefined;
};
/** The filters of the counts that wait (`listWaitingDifferencesInput`). */
export type WaitingDifferenceFilters = Pick<ListWaitingDifferencesInput, "reason">;

const PAGE_SIZE = 50;

const keys = {
  all: ["stock-adjustments"] as const,
  /** Prefix of every cached list: a write puts the adjustment it gets back in each of them. */
  lists: ["stock-adjustments", "list"] as const,
  /** Prefix of every cached list of the counts that wait. */
  waiting: ["stock-adjustments", "waiting"] as const,
};

export const stockAdjustmentQueries = {
  ...keys,

  list: (filters: StockAdjustmentFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          adjustments.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** With its lines: the units of the IMS, the ones counted, what it adds or takes. */
  detail: (adjustmentId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", adjustmentId],
      queryFn: ({ signal }) =>
        unwrap(adjustments[":adjustmentId"].$get({ param: { adjustmentId } }, { init: { signal } })),
    }),

  waiting: (filters: WaitingDifferenceFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.waiting, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          waiting.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),
};
